import {useRef, useState} from 'react';
import axios from 'axios';
import {useDispatch} from 'react-redux';
import {QUIKLEARN_BACKEND_URL} from 'config/app.config';
import {showQuotaPaywall} from 'store/subscriptionUi';
import {getWebAccessToken} from 'utils/webAuthSession';
import {buildTutorTokenRequest} from 'utils/tutorContext';
import {tutorRefusalPaywall} from 'utils/billing/billingModel';

const useFetchTokenLiveKit = () => {
  const dispatch = useDispatch();
  const [token, setToken] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const quotaBlockedRef = useRef(false);

  /**
   * Fetch a LiveKit token.
   *
   * IMPORTANT: This function is intentionally **non-throwing**. It returns both the token and
   * the structured failure so its caller never has to wait for React state to rerender.
   *
   * Reason: historically, callers didn't consistently `.catch()` token fetch
   * failures, which can surface as disruptive overlays/unhandled promise
   * rejections in the web app when the token service is down.
   */
  const getToken = async request => {
    setLoading(true);
    quotaBlockedRef.current = false;
    try {
      const accessToken = getWebAccessToken();
      if (!accessToken) throw new Error('Your web learning session has expired. Please reopen the course.');
      const response = await axios.post(
        `${QUIKLEARN_BACKEND_URL}/api/user/tutor-sessions/token`,
        buildTutorTokenRequest(request),
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      const nextToken = response?.data?.data?.token || response?.data?.token || null;
      setToken(nextToken);
      setError(null);
      return {token: nextToken, error: null};
    } catch (err) {
      // A unit outside the plan opens the locked-unit plans; a used-up meter, the quota plans
      // (Sol confirm-free-grade #1).
      const paywall = tutorRefusalPaywall(err?.response?.status, err?.response?.data, request?.mode);
      if (paywall) {
        quotaBlockedRef.current = true;
        dispatch(showQuotaPaywall(paywall));
      }
      console.error('Tutor token request failed', {status: err?.response?.status || null});
      const failure = {
        code: err?.response?.data?.code || null,
        message: err?.response?.data?.message || err?.message || 'An error occurred',
      };
      setError(failure);
      setToken(null);
      return {token: null, error: failure};
    } finally {
      setLoading(false);
    }
  };

  const clearToken = () => {
    setToken(null);
    setError(null);
  };

  return {
    token,
    error,
    loading,
    getToken,
    clearToken,
    wasQuotaBlocked: () => quotaBlockedRef.current,
  };
};

export default useFetchTokenLiveKit;
